import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

// Socket.IO client for real-time communication
import { io } from "socket.io-client";

// MUI Core components
import {
  Button,
  Container,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  IconButton,
  Chip,
  Typography,
  Paper,
  Box,
  CircularProgress,
  Divider,
  Tooltip,
  Skeleton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  useMediaQuery,
  Grid,
} from "@mui/material";

// Localization and theming hooks
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import { Helmet } from "react-helmet-async";

// API functions related to auctions
import {
  deleteAuctionById,
  getMyAuctions,
  initPublishAuction,
  extendAuction,
  draftAuction,
} from "../../api/auctions";

// Custom UI components
import CountdownTimer from "../../components/UI/CountdownTimer";
import MUIConfirm from "../../components/UI/MUIConfirm";
import MUIAlert from "../../components/UI/MUIAlert";

// Material UI Icons
import AddIcon from "@mui/icons-material/Add";
import ClockIcon from "@mui/icons-material/History";
import EditIcon from "@mui/icons-material/DriveFileRenameOutline";
import ExtendIcon from "@mui/icons-material/MoreTime";
import DownloadIcon from "@mui/icons-material/Download";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import DrafttIcon from "@mui/icons-material/DriveFileRenameOutline";
import DraftIcon from "@mui/icons-material/EventBusy";
import PendingIcon from "@mui/icons-material/WatchLater";
import ActiveIcon from "@mui/icons-material/Gavel";
import WinIcon from "@mui/icons-material/Celebration";
import DeleteIcon from "@mui/icons-material/Delete";
import AccountIcon from "@mui/icons-material/AccountBox";
import FrownIcon from "@mui/icons-material/SentimentDissatisfied";
import ArrowRightIcon from "@mui/icons-material/ArrowRightAlt";

const MyAuctions = () => {
  const theme = useTheme();
  const { t } = useTranslation();

  const [socket, setSocket] = useState(null);

  const [allAuctions, setAllAuctions] = useState([]);
  const [activeAuctions, setActiveAuctions] = useState([]);
  const [pendingAuctions, setPendingAuctions] = useState([]);
  const [pendingWinner, setPendingWinner] = useState([]);
  const [noWinner, setNoWinner] = useState([]);
  const [inactiveAuctions, setInactiveAuctions] = useState([]);
  const [completedAuctions, setCompletedAuctions] = useState([]);

  const [showArchived, setShowArchived] = useState(false);
  const [guideShown, setGuideShown] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [pendingPublishId, setPendingPublishId] = useState(null);
  const [showBillingDetailsWarning, setShowBillingDetailsWarning] =
    useState(false);

  const [loading, setLoading] = useState(true);
  const [submittingExtend, setSubmittingExtend] = useState(false);
  const [submittingDraft, setSubmittingDraft] = useState(false);
  const [submittingPublish, setSubmittingPublish] = useState(false);
  const [submittingDelete, setSubmittingDelete] = useState(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState("");
  const [confirmContent, setConfirmContent] = useState("");
  const [confirmCallback, setConfirmCallback] = useState(null);
  const [confirmParameter, setconfirmParameter] = useState(null);

  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState("success");

  const [error, setError] = useState(null);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const showAlert = (message, severity = "success") => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleAlertClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertOpen(false);
  };

  const showConfirm = (title, content, onConfirm, parameter) => {
    setConfirmTitle(title);
    setConfirmContent(content);
    setConfirmCallback(() => onConfirm);
    setconfirmParameter(parameter);
    setConfirmOpen(true);
  };

  const handleConfirmClose = () => {
    setConfirmOpen(false);
    setConfirmTitle("");
    setConfirmContent("");
    setConfirmCallback(null);
    setconfirmParameter(null);
  };

  const handleConfirmAction = () => {
    if (confirmCallback && confirmParameter) {
      confirmCallback(confirmParameter);
    }
    handleConfirmClose();
  };

  const fetchAuctions = async () => {
    try {
      const fetchedAuctions = await getMyAuctions();
      setAllAuctions(fetchedAuctions);
      setActiveAuctions(fetchedAuctions.filter((a) => a.status === "active"));
      setPendingWinner(
        fetchedAuctions.filter(
          (a) =>
            a.status === "completed" &&
            (Number(a.winner_id) < 0 || a.winner_id == null) &&
            Number(a.highest_bid) > 0
        )
      );
      setNoWinner(
        fetchedAuctions.filter(
          (a) =>
            a.status === "completed" &&
            (Number(a.highest_bid) <= 0 || a.highest_bid == null)
        )
      );
      setPendingAuctions(fetchedAuctions.filter((a) => a.status === "pending"));
      setInactiveAuctions(
        fetchedAuctions.filter((a) => a.status === "inactive")
      );
      setCompletedAuctions(
        fetchedAuctions.filter((a) => a.status === "completed").reverse()
      );
      setLoading(false);
    } catch (error) {
      setError(t("errors.err"));
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAuctions();

    if (localStorage.getItem("showBillingDetailsWarning")) {
      setShowBillingDetailsWarning(true);
    }

    const socket = io(process.env.REACT_APP_API_URL, {
      transports: ["websocket"], // or ['polling', 'websocket'] if fallback needed
      withCredentials: true,
    });
    setSocket(socket);

    socket.on("connect", () => {});

    socket.on("new_bid", (data) => {
      setActiveAuctions((prevAuctions) => {
        return prevAuctions.map((bid) => {
          if (bid.auction_id == data.auctionId) {
            if (data.bidAmount > (bid.highest_bid || 0)) {
              return { ...bid, highest_bid: data.bidAmount };
            }
          }
          return bid;
        });
      });
    });

    socket.on("auction_published", (data) => {
      fetchAuctions();
    });
    socket.on("auction_ended", (data) => {
      fetchAuctions();
    });
    socket.on("auction_updated", (data) => {
      fetchAuctions();
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  if (error) {
    return (
      <div>
        {t("my_auctions.error_message")}: {error}
      </div>
    );
  }

  const handleDeleteAuction = async (auctionId) => {
    showConfirm(
      t("my_auctions.delete_confirmation_title"),
      t("my_auctions.delete_confirmation"),
      async (auctionId) => {
        setSubmittingDelete(true);
        try {
          await deleteAuctionById(auctionId);
          fetchAuctions(); // Refresh the auctions list after deletion
          showAlert(t("my_auctions.delete_success"));
          setSubmittingDelete(false);
        } catch (error) {
          showAlert(t("my_auctions.delete_failure"), "error");
          setError(t("errors.err"));
          setSubmittingDelete(false);
        }
      },
      auctionId
    );
  };

  const handleGuideAction = async () => {
    setGuideOpen(false);
    setGuideShown(true);
    handlePublishAuction(pendingPublishId);
  };

  const hideBillingDetailsWarning = async () => {
    localStorage.removeItem("showBillingDetailsWarning");
  };

  const handlePublishAuction = async (auctionId) => {
    const showPublishMessage = localStorage.getItem("showPublishingMessage");

    if (showPublishMessage == true && !guideShown && !guideOpen) {
      setGuideOpen(true);
      setPendingPublishId(auctionId);
    } else {
      showConfirm(
        t("my_auctions.publish_confirmation_title"),
        t("my_auctions.publish_confirmation"),
        async (auctionId) => {
          setSubmittingPublish(true);
          try {
            await initPublishAuction(auctionId);
            fetchAuctions();
            localStorage.setItem("showPublishingMessage", false);
            showAlert(t("my_auctions.publish_success"));
            setSubmittingPublish(false);
          } catch (error) {
            showAlert(t("my_auctions.publish_failure"), "error");
            setError(t("errors.err"));
            setSubmittingPublish(false);
          }
        },
        auctionId
      );
    }
  };

  const handleExtendAuction = async (auctionId) => {
    showConfirm(
      t("my_auctions.extend_confirmation_title"),
      t("my_auctions.extend_confirmation"),
      async (auctionId) => {
        setSubmittingExtend(true); // Set loading to true while extending
        try {
          await extendAuction(auctionId);
          fetchAuctions();
          showAlert(t("my_auctions.extend_success"));
          setSubmittingExtend(false); // Set loading to true while extending
        } catch (error) {
          showAlert(t("my_auctions.extend_failure"), "error");
          setError(t("errors.err"));
          setSubmittingExtend(false); // Set loading to true while extending
        }
      },
      auctionId
    );
  };

  const handleDraftAuction = async (auctionId) => {
    showConfirm(
      t("my_auctions.draft_confirmation_title"),
      t("my_auctions.draft_confirmation"),
      async (auctionId) => {
        setSubmittingDraft(true);
        try {
          await draftAuction(auctionId);
          fetchAuctions();
          showAlert(t("my_auctions.draft_success"));
          setSubmittingDraft(false);
        } catch (error) {
          showAlert(t("my_auctions.draft_failure"), "error");
          setError(t("errors.err"));
          setSubmittingDraft(false);
        }
      },
      auctionId
    );
  };

  function handleDownloadInvoice(invoiceFilename, invoiceFile) {
    try {
      // Decode the base64 string to binary data
      const binaryData = atob(invoiceFile); // Decode base64

      // Convert the binary data to a byte array
      const byteArray = new Uint8Array(binaryData.length);
      for (let i = 0; i < binaryData.length; i++) {
        byteArray[i] = binaryData.charCodeAt(i);
      }

      // Create a blob from the byte array
      const blob = new Blob([byteArray], { type: "application/pdf" });

      // Create a temporary URL for the Blob
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob); // Create a URL for the blob
      link.download = invoiceFilename; // Set the download file name
      link.click(); // Simulate a click to trigger the download

      // Optionally revoke the object URL after the download to free up memory
      URL.revokeObjectURL(link.href);
    } catch (error) {
      alert(t("my_auctions.download_invoice_failure"));
    }
  }

  return (
    <>
      <Helmet>
        <title>{t("seo.title.my_auctions")}</title>
        <meta property="og:title" content={t("seo.title.my_auctions")} />

        <meta name="robots" content="noindex" />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <Container
        maxWidth="lg"
        sx={{
          mt: {
            xs: 4,
            md: 6,
          },
          mb: {
            xs: 4,
            md: 6,
          },
        }}
      >
        {showBillingDetailsWarning && allAuctions.length > 0 ? (
          <Alert
            variant="outlined"
            severity="info"
            sx={{
              mb: 2,
              backgroundColor: "info.background",
            }}
            action={
              <Button
                onClick={hideBillingDetailsWarning}
                component={Link}
                to="/profils?tab=payment"
                sx={{ textAlign: "center" }}
                color="inherit"
                size="small"
              >
                {t("my_auctions.incomplete_profile_cta")}
              </Button>
            }
          >
            {t("my_auctions.incomplete_profile")}
          </Alert>
        ) : null}

        <Dialog
          open={guideOpen}
          onClose={handleGuideAction}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          maxWidth="lg"
        >
          <DialogTitle id="alert-dialog-title">
            {t("my_auctions.guide_title")}
          </DialogTitle>
          <DialogContent>
            <DialogContentText id="alert-dialog-description">
              {t("my_auctions.guide_subtitle")} <br />
              <br />
              {t("my_auctions.guide_1")}
              <br />
              {t("my_auctions.guide_2")}
              <br />
              {t("my_auctions.guide_3")}
              <br />
              {t("my_auctions.guide_4")}
              <br />
              <br />
              {t("my_auctions.guide_why")}
              <br />
              {t("my_auctions.guide_contact")}
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button variant="contained" onClick={handleGuideAction}>
              {t("general.continue")}
            </Button>
          </DialogActions>
        </Dialog>
        <MUIConfirm
          open={confirmOpen}
          onClose={handleConfirmClose}
          title={confirmTitle}
          content={confirmContent}
          onConfirm={handleConfirmAction}
        />

        <MUIAlert
          open={alertOpen}
          onClose={handleAlertClose}
          severity={alertSeverity}
          message={alertMessage}
        />
        {/* No Auctions */}
        {allAuctions.length <= 0 ? (
          <Box
            sx={{
              mt: {
                xs: 4,
                md: 14,
              },
              mb: {
                xs: 4,
                md: 12,
              },
            }}
          >
            <Typography color="primary" variant="h2" gutterBottom>
              {t("my_auctions.page_title")}
            </Typography>
            <Typography variant="body1">
              {t("my_auctions.no_auctions")}
            </Typography>
            <Button
              variant="contained"
              component={Link}
              to="/izveidot"
              color="success"
              endIcon={<ArrowRightIcon />}
              sx={{ mt: 4, mb: 3, color: "White" }}
            >
              {t("my_auctions.no_auctions_cta")}
            </Button>
          </Box>
        ) : (
          <Box sx={{ marginBottom: 4, pt: 4 }}>
            <Box sx={{ position: "relative" }}>
              <Typography color="primary" variant="h2" gutterBottom>
                {t("my_auctions.page_title")}
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                endIcon={<AddIcon />}
                component={Link}
                to="/izveidot"
                sx={{
                  position: "absolute",
                  top: 0,
                  right: 0,
                  borderRadius: "50px",
                  // STRIPE SHADOW
                  // boxShadow: '0px 0px 0px rgba(0,0,0,0.0)',
                  "&:hover": {
                    backgroundColor: theme.palette.secondary.light,
                    // boxShadow: '0px 2px 10px rgba(0, 0, 0, 0.08), 0px 1px 3px rgba(0, 0, 0, 0.05)',
                  },
                }}
              >
                {t("my_auctions.new_auction")}
              </Button>

              {loading ? (
                <Paper sx={{ padding: 2, marginBottom: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    <Skeleton width="40%" />
                  </Typography>
                  <List>
                    {Array.from({ length: 2 }).map((_, index) => (
                      <ListItem key={index} sx={{ marginBottom: 1 }}>
                        <ListItemText
                          primary={<Skeleton width="60%" />}
                          secondary={<Skeleton width="40%" />}
                        />
                        <ListItemText
                          primary={<Skeleton width="30%" />}
                          secondary={<Skeleton width="30%" />}
                        />
                        <ListItemSecondaryAction>
                          <Skeleton
                            width="100px"
                            height="40px"
                            style={{ marginRight: 1 }}
                          />
                          <Skeleton
                            width="80px"
                            height="40px"
                            style={{ marginRight: 1 }}
                          />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              ) : activeAuctions.length > 0 ||
                pendingAuctions.length > 0 ||
                pendingWinner.length > 0 ||
                noWinner.length > 0 ? (
                <Paper sx={{ padding: 2, marginBottom: 4 }}>
                  <Typography
                    color="primary"
                    variant="h6"
                    sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                  >
                    <ActiveIcon />
                    {t("my_auctions.active_auctions")}
                  </Typography>
                  {(pendingWinner.length > 0 || noWinner.length > 0) && (
                    <List sx={{ mb: 0, pb: 0 }}>
                      {pendingWinner.map((auction, index) => (
                        <ListItem
                          key={auction.auction_id}
                          component={Link}
                          to={`/rezultats/${auction.auction_id}`}
                          sx={{
                            backgroundColor: "secondary.background",
                            borderStyle: "solid",
                            borderWidth: "1px",
                            borderColor: "secondary.main",
                            marginBottom: 1,
                            marginTop: index === 0 ? 1 : 0,
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "flex-start", sm: "center" },
                            gap: 1,
                            p: 2,
                          }}
                        >
                          {/* Icon + Text Container */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexGrow: 1,
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: "unset",
                                mr: 1,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <WinIcon color="secondary" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                auction.property_name
                                  ? auction.property_name
                                  : `${t("general.auction")} ${
                                      auction.auction_id
                                    }`
                              }
                              secondary={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: theme.palette.secondary.dark,
                                    fontSize: {
                                      xs: "0.9rem",
                                      sm: "0.875rem",
                                    },
                                  }}
                                >
                                  {t("my_auctions.winner_pending")}
                                </Typography>
                              }
                              sx={{
                                color: theme.palette.secondary.dark,
                              }}
                            />
                          </Box>

                          {/* Button Container */}
                          <Box
                            sx={{
                              mt: { xs: 1, sm: 0 },
                              width: { xs: "100%", sm: "auto" },
                              display: "flex",
                              justifyContent: {
                                xs: "flex-start",
                                sm: "flex-end",
                              },
                            }}
                          >
                            <Button
                              variant="outlined"
                              component={Link}
                              fullWidth
                              color="secondary"
                              to={`/rezultats/${auction.auction_id}`}
                              sx={{
                                fontSize: { xs: "0.85rem", sm: "0.875rem" },
                                color: theme.palette.secondary.main,
                                borderColor: theme.palette.secondary.main,
                                "&:hover": {
                                  color: theme.palette.secondary.contrastText,
                                  backgroundColor: theme.palette.secondary.main,
                                },
                                whiteSpace: "nowrap",
                              }}
                              endIcon={<EmojiEventsIcon />}
                            >
                              {submittingExtend ? (
                                <CircularProgress size={20} color="inherit" />
                              ) : (
                                t("my_auctions.cta_winner_pending")
                              )}
                            </Button>
                          </Box>
                        </ListItem>
                      ))}

                      {noWinner.map((auction, index) => (
                        <ListItem
                          key={auction.auction_id}
                          component={Link}
                          to={`/izsole/${auction.auction_id}`}
                          sx={{
                            backgroundColor: "error.background",
                            borderStyle: "solid",
                            borderWidth: "1px",
                            borderColor: "error.main",
                            marginBottom: 1,
                            marginTop: index === 0 ? 1 : 0,
                            display: "flex",
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "flex-start", sm: "center" },
                            gap: 1,
                            p: 2,
                          }}
                        >
                          {/* Icon + Text Container */}
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              flexGrow: 1,
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                minWidth: "unset",
                                mr: 1,
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <FrownIcon color="error" />
                            </ListItemIcon>
                            <ListItemText
                              primary={
                                auction.property_name
                                  ? auction.property_name
                                  : `${t("general.auction")} ${
                                      auction.auction_id
                                    }`
                              }
                              secondary={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: theme.palette.error.main,
                                    fontSize: {
                                      xs: "0.9rem",
                                      sm: "0.875rem",
                                    },
                                  }}
                                >
                                  {t("my_auctions.no_winner")}
                                </Typography>
                              }
                              sx={{
                                color: theme.palette.error.main,
                              }}
                            />
                          </Box>

                          {/* Button Container */}
                          <Box
                            sx={{
                              mt: { xs: 1, sm: 0 },
                              width: { xs: "100%", sm: "auto" },
                              display: "flex",
                              gap: 1,
                              justifyContent: {
                                xs: "flex-start",
                                sm: "flex-end",
                              },
                            }}
                          >
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={(e) => {
                                e.preventDefault(); // prevent navigation if ListItem is clickable
                                handleDraftAuction(auction.auction_id);
                              }}
                              endIcon={
                                submittingDraft ? (
                                  <CircularProgress size={24} color="inherit" />
                                ) : (
                                  <DraftIcon />
                                )
                              }
                              sx={{ width: { xs: "100%", sm: "auto" } }}
                            >
                              {t("my_auctions.to_drafts")}
                            </Button>
                            <Button
                              variant="outlined"
                              color="error"
                              onClick={(e) => {
                                e.preventDefault(); // prevent navigation on button click
                                handleExtendAuction(auction.auction_id);
                              }}
                              endIcon={<ExtendIcon />}
                              sx={{ width: { xs: "100%", sm: "auto" } }}
                            >
                              {submittingExtend ? (
                                <CircularProgress size={24} color="inherit" />
                              ) : (
                                t("my_auctions.extend")
                              )}
                            </Button>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  )}
                  <List sx={{ width: "100%" }}>
                    {activeAuctions.map((auction, index) => (
                      <>
                        <ListItem
                          button
                          key={auction.auction_id}
                          component={Link}
                          to={`/izsole/${auction.auction_id}`}
                          sx={{
                            mb: 1,
                            flexDirection: { xs: "column", sm: "row" }, // Stack on mobile
                            alignItems: { xs: "flex-start", sm: "center" },
                            width: "100%",
                            "&:hover": {
                              backgroundColor: theme.palette.primary.superlight,
                            },
                          }}
                        >
                          {/* Left: Property name + chip */}
                          <Box
                            sx={{
                              flexGrow: 0.4,
                              flexBasis: 0,
                              width: "100%", // Force full width on mobile
                              display: "flex",
                              flexDirection: "column",
                              mb: { xs: 1, sm: 0 },
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              {auction.property_name
                                ? auction.property_name
                                : `${t("general.auction")} ${
                                    auction.auction_id
                                  }`}
                              <Chip
                                label={t("my_auctions.auction_running")}
                                size="small"
                                variant="contained"
                                sx={{
                                  ml: 1,
                                  px: 0.5,
                                  py: 0,
                                  backgroundColor:
                                    theme.palette.success.background2,
                                  border: `1px solid ${theme.palette.success.main}`,
                                  color: theme.palette.success.main,
                                  fontSize: "0.7em",
                                }}
                              />
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              <CountdownTimer endTime={auction.end_time} />
                            </Typography>
                          </Box>

                          {/* Center: Price */}
                          <Box
                            sx={{
                              flexGrow: 1,
                              flexBasis: 0,
                              width: "100%", // Force full width on mobile
                              display: "flex",
                              flexDirection: "column",
                              mb: { xs: 1, sm: 0 },
                              textAlign: { xs: "left", sm: "center" },
                            }}
                          >
                            <Typography variant="body1">
                              €
                              {Number(auction.highest_bid) > 0
                                ? Number(auction.highest_bid).toLocaleString()
                                : Number(
                                    auction.starting_price
                                  ).toLocaleString()}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {t("my_auctions.start_price")}:{" "}
                              {auction.starting_price} €
                            </Typography>
                          </Box>

                          {/* Right: Buttons */}
                          <Box
                            sx={{
                              flexBasis: 0,
                              width: "100%", // Force full width on mobile
                              display: "flex",
                              flexDirection: "column",
                              mb: { xs: 1, sm: 0 },
                              flexDirection: { xs: "column", sm: "row" },
                              gap: 1,
                              width: "100%",
                              ml: { sm: "auto" }, // Push to right on desktop
                            }}
                          >
                            <Button
                              variant="outlined"
                              color="primary"
                              disabled={
                                new Date(auction.end_time) >
                                new Date(Date.now() + 60 * 24 * 60 * 60 * 1000)
                              }
                              onClick={(e) => {
                                e.preventDefault(); // prevent navigation on button click
                                handleExtendAuction(auction.auction_id);
                              }}
                              endIcon={<ExtendIcon />}
                              sx={{ width: { xs: "100%", sm: "auto" } }}
                            >
                              {submittingExtend ? (
                                <CircularProgress size={24} color="inherit" />
                              ) : (
                                t("my_auctions.extend")
                              )}
                            </Button>
                            {/* <Button
                                                        variant="outlined"
                                                        color="primary"
                                                        component={Link}
                                                        to={`/labot/${auction.auction_id}`}
                                                        endIcon={<EditIcon />}
                                                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                                                    >
                                                        {t("my_auctions.edit")}
                                                    </Button> */}
                          </Box>
                        </ListItem>
                        {isMobile && index < activeAuctions.length - 1 && (
                          <Divider sx={{ my: 3 }} />
                        )}
                      </>
                    ))}
                  </List>
                  {pendingAuctions.length > 0 && (
                    <List>
                      {pendingAuctions.length > 0 ? (
                        <Divider sx={{ mb: 2 }} />
                      ) : null}
                      {pendingAuctions.map((auction) => (
                        <ListItem
                          button
                          key={auction.auction_id}
                          sx={{
                            mb: 1,
                            flexDirection: { xs: "column", sm: "row" },
                            alignItems: { xs: "flex-start", sm: "center" },
                            "&:hover": {
                              backgroundColor: theme.palette.primary.superlight,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                              mb: { xs: 1, sm: 0 },
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                flexWrap: "wrap",
                              }}
                            >
                              {t("general.auction")} {auction.auction_id}
                              <Chip
                                sx={{
                                  ml: 1,
                                  px: 0,
                                  py: 0,
                                  backgroundColor:
                                    theme.palette.secondary.background,
                                  borderStyle: "solid",
                                  borderWidth: "1px",
                                  borderColor: theme.palette.secondary.main,
                                  color: theme.palette.secondary.main,
                                  fontSize: "0.7em",
                                }}
                                label={t("my_auctions.auction_pending")}
                                icon={<PendingIcon />}
                                size="small"
                                color="secondary"
                                variant="contained"
                              />
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {t("my_auctions.auction_pending")}
                            </Typography>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: { xs: "column", sm: "row" },
                              gap: 1,
                              ml: { sm: "auto" },
                              width: { xs: "100%", sm: "auto" },
                            }}
                          >
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={(e) => {
                                e.preventDefault(); // prevent navigation if ListItem is clickable
                                handleDraftAuction(auction.auction_id);
                              }}
                              endIcon={
                                submittingDraft ? (
                                  <CircularProgress size={24} color="inherit" />
                                ) : (
                                  <DraftIcon />
                                )
                              }
                              sx={{ width: { xs: "100%", sm: "auto" } }}
                            >
                              {t("my_auctions.draft")}
                            </Button>
                            <Button
                              variant="outlined"
                              color="primary"
                              onClick={(e) => {
                                e.preventDefault(); // prevent navigation if ListItem is clickable
                                handleDownloadInvoice(
                                  auction.invoice_filename,
                                  auction.invoice_file
                                );
                              }}
                              endIcon={<DownloadIcon />}
                              sx={{ width: { xs: "100%", sm: "auto" } }}
                            >
                              {t("my_auctions.download_invoice")}
                            </Button>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              ) : (
                <></>
              )}

              {loading ? (
                <Paper sx={{ padding: 2, marginBottom: 4 }}>
                  <Typography variant="h6" gutterBottom>
                    <Skeleton width="40%" />
                  </Typography>
                  <List>
                    {Array.from({ length: 2 }).map((_, index) => (
                      <ListItem key={index} sx={{ marginBottom: 1 }}>
                        <ListItemText
                          primary={<Skeleton width="60%" />}
                          secondary={<Skeleton width="40%" />}
                        />
                        <ListItemSecondaryAction>
                          <Skeleton
                            width="120px"
                            height="40px"
                            style={{ marginRight: 1 }}
                          />
                          <Skeleton
                            width="80px"
                            height="40px"
                            style={{ marginRight: 1 }}
                          />
                          <Skeleton width="80px" height="40px" />
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              ) : inactiveAuctions.length > 0 ? (
                <Paper sx={{ padding: 2, marginBottom: 4 }}>
                  <Typography
                    color="primary"
                    variant="h6"
                    sx={{ display: "flex", alignItems: "center", gap: 0 }}
                  >
                    <DraftIcon />
                    {t("my_auctions.drafts")}
                  </Typography>
                  <List>
                    {inactiveAuctions.map((auction) => (
                      <ListItem
                        button
                        key={auction.auction_id}
                        component={Link}
                        to={`/izsole/${auction.auction_id}`}
                        sx={{
                          marginBottom: 1,
                          "&:hover": {
                            backgroundColor: theme.palette.primary.superlight,
                          },
                        }}
                      >
                        <ListItemText
                          primary={
                            auction.property_name
                              ? auction.property_name
                              : `${t("general.auction")} ${auction.auction_id}`
                          }
                          secondary={`${t("general.auction")} Nr. ${
                            auction.auction_id
                          }`}
                          sx={{ color: theme.palette.secondary.primary }}
                        />
                        <ListItemSecondaryAction>
                          <IconButton
                            // variant="contained"
                            color="primary"
                            component={Link}
                            to={`/labot/${auction.auction_id}`}
                            size="small"
                            sx={{
                              marginRight: 1,
                              backgroundColor: theme.palette.primary.superlight,
                              borderStyle: "solid",
                              borderWidth: "1px",
                              borderColor: theme.palette.primary.superlight2,
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.primary.superlight2,
                              },
                            }}
                          >
                            <EditIcon />
                          </IconButton>

                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() =>
                              handleDeleteAuction(auction.auction_id)
                            }
                            sx={{
                              marginRight: 2,
                              backgroundColor: theme.palette.primary.superlight,
                              borderStyle: "solid",
                              borderWidth: "1px",
                              borderColor: theme.palette.primary.superlight2,
                              "&:hover": {
                                backgroundColor:
                                  theme.palette.primary.superlight2,
                              },
                            }}
                          >
                            {submittingDelete ? (
                              <CircularProgress size={24} color="inherit" />
                            ) : (
                              <DeleteIcon />
                            )}
                          </IconButton>

                          <Button
                            variant="contained"
                            color="success"
                            onClick={() =>
                              handlePublishAuction(auction.auction_id)
                            }
                            sx={{ marginRight: 1 }}
                            // endIcon={<PublishIcon />}
                          >
                            {submittingPublish ? (
                              <CircularProgress size={24} color="inherit" />
                            ) : (
                              t("my_auctions.publish")
                            )}
                          </Button>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              ) : null}

              <Button
                variant="outlined"
                onClick={() => setShowArchived((prev) => !prev)}
                endIcon={<ClockIcon />}
                sx={{
                  borderRadius: "50px",
                  color: "gray",
                  borderColor: "gray",
                  marginTop: 2,
                }}
                disabled={loading}
              >
                {t("my_auctions.view_archive")}
              </Button>

              {showArchived &&
                (loading ? (
                  <Paper sx={{ padding: 2, marginTop: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      <Skeleton width="40%" />
                    </Typography>
                    <List>
                      {Array.from({ length: 2 }).map((_, index) => (
                        <ListItem key={index} sx={{ marginBottom: 1 }}>
                          <ListItemText
                            primary={<Skeleton width="60%" />}
                            secondary={<Skeleton width="50%" />}
                          />
                          <ListItemText
                            primary={<Skeleton width="30%" />}
                            secondary={<Skeleton width="30%" />}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                ) : completedAuctions.length > 0 ? (
                  <Paper sx={{ padding: 2, marginTop: 4 }}>
                    <Typography variant="h6" gutterBottom>
                      {t("my_auctions.archive_title")}
                    </Typography>
                    <List>
                      {/* 🔹 Header row */}
                      {/* <ListItem
                        sx={{
                          marginBottom: 1,
                          backgroundColor: theme.palette.primary.superlight2,
                          fontWeight: "bold",
                        }}
                      >
                        <ListItemText
                          primary={t("general.property")}
                          sx={{ color: theme.palette.text.primary }}
                        />
                        <ListItemText
                          primary={t("general.final_price")}
                          sx={{ color: theme.palette.text.primary }}
                        />
                        <ListItemText
                          primary={t("general.winner")}
                          sx={{
                            color: theme.palette.text.primary,
                            textAlign: "right",
                          }}
                        />
                      </ListItem> */}

                      {/* 🔹 Data rows */}
                      {completedAuctions.map((auction) => (
                        <ListItem
                          key={auction.auction_id}
                          button
                          component={Link}
                          to={`/izsole/${auction.auction_id}`}
                          sx={{
                            marginBottom: 1,
                            "&:hover": {
                              backgroundColor: theme.palette.primary.superlight,
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              auction.property_name
                                ? auction.property_name
                                : `${t("general.auction")} ${
                                    auction.auction_id
                                  }`
                            }
                            secondary={`${t(
                              "my_auctions.end_date"
                            )}: ${new Date(auction.end_time).toLocaleString()}`}
                            sx={{ color: theme.palette.text.primary }}
                          />

                          <ListItemText
                            primary={`€${
                              Number(auction.highest_bid) > 0
                                ? Number(auction.highest_bid).toLocaleString()
                                : Number(
                                    auction.starting_price
                                  ).toLocaleString()
                            }`}
                            secondary={`${t("my_auctions.start_price")}: ${
                              auction.starting_price
                            } €`}
                            sx={{ color: theme.palette.text.primary }}
                          />

                          {/* ✅ Winner info (only if exists) */}
                          {auction.winner_id && (
                            <ListItemText
                              primary={
                                `${auction.winner_first_name || ""} ${
                                  auction.winner_last_name || ""
                                }`.trim() +
                                (auction.winner_business_name
                                  ? " (" + auction.winner_business_name + ")"
                                  : "")
                              }
                              secondary={`${auction.winner_email || ""}${
                                auction.winner_phone
                                  ? " • " + auction.winner_phone
                                  : ""
                              }`}
                              sx={{
                                color: theme.palette.text.secondary,
                                textAlign: "right",
                              }}
                            />
                          )}
                        </ListItem>
                      ))}
                    </List>
                  </Paper>
                ) : null)}
            </Box>
          </Box>
        )}
      </Container>
    </>
  );
};

export default MyAuctions;
